<template>
    <div
        v-if="sentences"
        data-testid="pending-late-deduction-notice"
        role="status"
        class="tw:rounded-md tw:border tw:border-amber-600 tw:bg-amber-50 tw:p-3 tw:text-sm tw:text-amber-950 tw:dark:border-amber-400 tw:dark:bg-amber-950 tw:dark:text-amber-100"
    >
        <p class="tw:m-0 tw:break-words" data-testid="pending-late-deduction-summary">{{ sentences.summary }}</p>
        <p class="tw:m-0 tw:mt-1 tw:break-words" data-testid="pending-late-deduction-policy">{{ sentences.policy }}</p>
        <p
            v-if="sentences.zero"
            class="tw:m-0 tw:mt-1 tw:font-semibold tw:break-words"
            data-testid="pending-late-deduction-zero"
        >
            {{ sentences.zero }}
        </p>
    </div>
</template>

<script setup>
/**
 * Non-blocking notice on a learner's submission screen once the activity is past due and a
 * late policy applies. It states the percentage the learner would lose, in the same words
 * as the grade screens, and never gets in the way of submitting. The server decides
 * whether anything applies; no notice renders when it answers null.
 */
import moment from 'moment-timezone';
import { computed, onMounted, watch } from 'vue';

import { usePendingLateDeduction } from '@/composables/usePendingLateDeduction.js';
import { initialState } from '@/Helpers.js';
import { formatInstitutionDatetime } from '@/utilities/formatInstitutionDatetime.js';
import { pendingLateDeductionSentences } from '@/utilities/lateDeductionDisplay.js';

defineOptions({ name: 'PendingLateDeductionNotice' });

const props = defineProps({
    activityId: { type: [Number, String], default: null },
});

const { pending, load } = usePendingLateDeduction();

const sentences = computed(() => {
    if (!pending.value) {
        return null;
    }

    const dueLabel = formatInstitutionDatetime({
        moment,
        timezone: initialState('InstitutionSettings.timezone'),
        date: pending.value.due_at,
        format: 'MMM D, h:mm A',
        emptyValue: 'its due date',
    });

    return pendingLateDeductionSentences(pending.value, dueLabel);
});

onMounted(() => load(props.activityId));
watch(
    () => props.activityId,
    id => load(id)
);
</script>
